/**
 * A multi-recipient email field, for the "Can edit" half of the compose.
 *
 * SH-7. NOT promoted to `ui/components.js`, deliberately and with the reason
 * recorded: there is no second consumer, and the house bar is that a shared
 * sibling earns promotion when two surfaces build it. It lives here so that
 * dropping it is a file deletion and one call-site line.
 *
 * ── Two scope decisions ───────────────────────────────────────────────────
 *
 * ONE FIELD, TWO VOCABULARIES. The compose is a single chip field for both
 * access types, so the choice is a property of the invitation rather than a
 * different form — but the DELIMITER is not shared: a reviewer name legitimately
 * contains a space ("Dana Whitcomb") and an address never does. So whitespace
 * separates in edit mode and is an ordinary character in comment mode; comma and
 * semicolon separate in both. That is what `splitter` and `validate` are for,
 * and it is why they are options rather than constants.
 *
 * THE SEAT CAP IS THE REASON THIS IS CAREFUL. `MAX_MEMBERS_PER_PROJECT` is 5,
 * and the server's ATOMIC INSERT counts members PLUS live invites while its
 * pre-read counts members alone — so the ceiling is roughly four chips, and N
 * chips are N sequential requests with N independent failure codes. Two
 * independent reviews recommended cutting this field for exactly that reason.
 * It is kept by explicit decision, and the price is paid here: capacity is
 * stated BEFORE the send, outcomes are reported PER RECIPIENT, and a partial
 * failure never loses the successes.
 */

import { createButton } from '../components.js';
import { icon } from '../icons.js';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

export function isValidRecipient(value) {
  return EMAIL_RE.test(String(value || '').trim());
}

/**
 * @param {object} [opts]
 * @param {(values: string[]) => void} [opts.onChange]
 * @param {string} [opts.testid]
 * @param {(value: string) => boolean} [opts.validate]  per-chip validity
 * @param {RegExp} [opts.splitter]                      what separates two entries
 * @param {string} [opts.placeholder]
 * @param {string} [opts.ariaLabel]
 * @param {string} [opts.inputType]
 * @returns {{el: HTMLElement, input: HTMLInputElement, values: () => string[],
 *            commit: () => void, remove: (value: string) => void, clear: () => void,
 *            keep: (values: string[]) => void, setMode: (mode: object) => void}}
 */
export function createRecipientField({
  onChange = null,
  testid = '',
  validate = isValidRecipient,
  splitter = /[\s,;]+/,
  placeholder = 'collaborator@example.com',
  ariaLabel = 'Email addresses',
  inputType = 'email'
} = {}) {
  const chips = [];
  let armed = false;
  let mode = { validate, splitter, placeholder, ariaLabel, inputType };

  const el = document.createElement('div');
  el.className = 'pb-share-chips';

  const input = document.createElement('input');
  input.type = mode.inputType;
  input.className = 'pb-share-chips-input';
  // Never placeholder-only: the kit's own rule, and the label is what a screen
  // reader announces. The placeholder must also not begin "Search"/"Filter" —
  // `picker-patterns-gate-tests` treats that as a search field and would then
  // demand an `initialFocus` this component cannot supply.
  input.setAttribute('aria-label', mode.ariaLabel);
  input.placeholder = mode.placeholder;
  if (testid) input.dataset.testid = testid;

  const notify = () => onChange?.(values());
  const values = () => chips.slice();

  function paint() {
    for (const node of [...el.querySelectorAll('.pb-share-chip')]) node.remove();
    chips.forEach((value, index) => {
      const chip = document.createElement('span');
      chip.className = 'pb-share-chip';
      if (!mode.validate(value)) chip.classList.add('is-invalid');
      if (armed && index === chips.length - 1) chip.classList.add('is-armed');
      const label = document.createElement('span');
      label.textContent = value;
      chip.appendChild(label);
      const remove = createButton({
        variant: 'icon',
        className: 'pb-share-chip-x',
        icon: icon('close', { size: 'sm' }),
        ariaLabel: `Remove ${value}`,
        onClick: () => {
          chips.splice(index, 1);
          armed = false;
          paint();
          notify();
          input.focus();
        }
      });
      chip.appendChild(remove);
      el.insertBefore(chip, input);
    });
  }

  /**
   * In edit mode: whitespace, comma and semicolon all separate — an address
   * contains none. In comment mode a name may contain a space, so only comma
   * and semicolon do.
   */
  function commit() {
    const raw = input.value.trim().replace(/[,;]+$/, '');
    if (!raw) return;
    for (const piece of raw
      .split(mode.splitter)
      .map((s) => s.trim())
      .filter(Boolean)) {
      if (!chips.some((c) => c.toLowerCase() === piece.toLowerCase())) chips.push(piece);
    }
    input.value = '';
    armed = false;
    paint();
    notify();
  }

  el.addEventListener('mousedown', (event) => {
    if (event.target === el) {
      event.preventDefault();
      input.focus();
    }
  });

  const endsWithSeparator = () => {
    const tail = input.value.slice(-1);
    return tail !== '' && mode.splitter.test(tail);
  };

  input.addEventListener('input', () => {
    if (endsWithSeparator()) commit();
    else notify();
  });

  input.addEventListener('paste', (event) => {
    const text = (event.clipboardData || window.clipboardData)?.getData('text') || '';
    if (!mode.splitter.test(text)) return;
    // The case this field exists for: an agency pasting a list.
    event.preventDefault();
    input.value = text;
    commit();
  });

  input.addEventListener('blur', () => {
    armed = false;
    // A typed-but-uncommitted address that vanishes when you click the button is
    // the classic chip-field bug.
    if (input.value.trim()) commit();
    else paint();
  });

  input.addEventListener('keydown', (event) => {
    if (event.key === 'Enter') {
      // Enter commits a pending address; the FORM's submit handler decides what
      // an empty field means, so it is not swallowed here.
      if (input.value.trim()) {
        event.preventDefault();
        commit();
      }
      return;
    }
    if (event.key === 'Backspace' && !input.value && chips.length) {
      event.preventDefault();
      // Two-step: arm, then remove. A single backspace silently deleting a chip
      // you cannot see being selected is how addresses go missing.
      if (armed) {
        chips.pop();
        armed = false;
        notify();
      } else {
        armed = true;
      }
      paint();
    }
  });

  el.appendChild(input);

  return {
    el,
    input,
    values,
    commit,
    remove(value) {
      const index = chips.indexOf(value);
      if (index >= 0) {
        chips.splice(index, 1);
        paint();
        notify();
      }
    },
    /** Keep exactly these — used to leave the failures behind after a partial send. */
    keep(next) {
      chips.length = 0;
      for (const value of next) chips.push(value);
      armed = false;
      paint();
      notify();
    },
    clear() {
      chips.length = 0;
      input.value = '';
      armed = false;
      paint();
      notify();
    },
    /**
     * Switch vocabularies in place. The chips STAY — an address typed under
     * "Can edit" is exactly what you want carried across when the refusal
     * offers a review link instead, and re-validating them under the new rule
     * is the whole point of repainting.
     */
    setMode(next = {}) {
      mode = { ...mode, ...next };
      input.type = mode.inputType;
      input.placeholder = mode.placeholder;
      input.setAttribute('aria-label', mode.ariaLabel);
      paint();
    },
    isValid: (value) => mode.validate(value)
  };
}
